import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

export const Showreel = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Auto-pause when the video scrolls out of view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !video.paused) video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    setPlaying(true);
    const p = video.play();
    if (p?.catch) p.catch(() => setPlaying(false));
  };

  return (
    <section id="showreel" className="relative py-14 lg:py-20 bg-[#0D0D0D] border-y border-[#D4AF37]/25 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[500px] h-[300px] rounded-full bg-[#FF2A2A]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="font-heading heading-fluid font-medium tracking-normal text-metal">
          Watch Robust Entertainment in Action
        </h2>
        <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-3xl mx-auto">
          From production shows and character experiences to live entertainment, technical installations and branded
          visual content, our work is designed to look professional, feel exciting and deliver a memorable guest
          experience.
        </p>
        <p className="mt-4 text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto">
          See how creative development, live performance, visual content and technical production come together to create
          entertainment experiences that engage guests and elevate venues.
        </p>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14 rounded-3xl p-[2px] overflow-hidden bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#8a6a10] shadow-[0_0_40px_rgba(212,175,55,0.18)]"
        >
          <div className="relative rounded-[22px] overflow-hidden bg-black">
          <video
            ref={videoRef}
            src="/robust-showreel.mp4"
            poster="/robust-showreel-poster.jpg"
            controls={playing}
            playsInline
            preload="metadata"
            onEnded={() => setPlaying(false)}
            className="w-full aspect-video object-cover bg-black"
            data-testid="showreel-video"
          />
          {!playing && (
            <>
              <div className="absolute inset-0 bg-black/40 pointer-events-none" />
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  data-testid="showreel-play-button"
                  onClick={startPlayback}
                  className="group flex items-center gap-4 bg-black/60 backdrop-blur-xl border border-white/20 rounded-full pl-3 pr-8 py-3 hover:border-[#FF2A2A]/60 transition-colors duration-300"
                >
                  <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#FF2A2A] group-hover:bg-[#FF4D4D] transition-colors duration-300 shadow-[0_0_30px_rgba(255,42,42,0.5)]">
                    <Play size={24} className="text-white ml-0.5" fill="white" />
                  </span>
                  <span className="text-white font-medium tracking-wide">Play Showreel</span>
                </button>
              </div>
            </>
          )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Showreel;
